import { observable, computed, toJS } from 'mobx'
import Model from '../shared/model'
import { deepGet } from '../shared/utils'

import validate from '../shared/decorators/model/validate'

const getValue = (value) => {
  return value === null || value === undefined
    ? null
    : value
}

@validate({
  museumId: 'required|number|min:1',

  // step general
  title: 'required|string|minLen:2',
  duration: 'required|number|min:10',
  maxParticipants: 'required|number', //|min:1
  description: 'required|minLen:100|maxLen:1000',
  expireAt: `required|date|greater:${new Date()}`,

  // step reference
  schools: 'required|array|minLen:1',
  // curriculums: 'required|string',
  grades: 'required|array|minLen:1',
  subjects: 'required|array|minLen:1',

  // step booking
  bookingStart: 'required|date',
  bookingEnd: 'required|date|greater:bookingStart',
  days: 'required|array|minLen:1',

  hostelUrl: 'url',

  // step media
  // links: 'required|array',
  // images: 'required|array|minLen:1'
})
export default class GuidedTour extends Model {

  @observable museumId = ''

  @observable title = ''

  @observable sanitizedOrderTitle = ''

  @observable description = ''

  @observable duration

  @observable maxParticipants

  @observable generalPrice = ''

  @observable minPrice = ''

  @observable costsByPerson = ''

  @observable bookingStart = '1970-01-01'

  // moment(start).format('YYYY-MM-DD')
  @observable bookingEnd = '1970-12-31' // moment(end).format('YYYY-MM-DD')

  @observable bookableOutOfSeason = false

  @observable bookableOutOfOpeningTimes = false

  @observable temporarilyNotBookable = false

  @observable temporarilyNotBookableReason = ''

  @observable specialConditionsForRemedialClasses = false

  @observable freeEntryForTeachersAtPrevisit = false

  @observable meetingPoint = ''

  @observable grades = []

  @observable subjects = []

  @observable expireAt = ''

  // moment(end).format('YYYY-MM-DD')
  @observable curriculums = ''

  @observable days = []

  @observable schools = []

  @observable languages = []

  @observable links = null

  @observable images = null

  @observable hostelUrl = ''

  @observable dirty = true

  @observable _message = {}

  @observable rejectedFields = []

  @computed get isReleaseable() {
    return this.isNew * 1 === 0
  }

  @computed get subtitle() {
    const museum = deepGet(this, 'museum.name') || '-'
    const location = deepGet(this, 'museum.address.location') || '-'
    return `${museum}, ${location}`
  }

  @computed get isDirty() {
    return !!this.dirty
  }

  constructor(...args) {
    super(...args)

    this.initValidate()

    this.type = 'guidedTour'
  }

  parseList(list) {
    return (list || []).map((itm) => {
      if (typeof itm === 'string' || typeof itm === 'number') {
        return {
          id: itm,
          value: itm,
          amount: undefined,
          attr: undefined
        }
      }

      return {
        id: itm.value !== undefined ? itm.value : itm.id,
        value: itm.value !== undefined ? itm.value : itm.id,
        amount: itm.pivot ? itm.pivot.amount : itm.attr,
        attr: itm.pivot ? itm.pivot.amount : itm.attr
      }
    })
  }

  parse(data) {
    if (data.schools) {
      data.schools = this.parseList(data.schools)
    }
    if (data.grades) {
      data.grades = this.parseList(data.grades)
    }
    if (data.subjects) {
      data.subjects = this.parseList(data.subjects)
    }
    if (data.days) {
      data.days = this.parseList(data.days)
    }
    if (data.methods) {
      data.methods = this.parseList(data.methods)
    }
    if (data.materials) {
      data.materials = this.parseList(data.materials)
    }
    if (data.languages) {
      data.languages = this.parseList(Array.isArray(data.languages)
        ? data.languages
        : (`${data.languages}` || '')
          .split(',')
          .map((str) => parseInt(str.trim(), 10)))
    }

    // convert boolean values to true bools
    [
      'bookableOutOfSeason',
      'bookableOutOfOpeningTimes',
      'freeEntryForTeachersAtPrevisit',
      'specialConditionsForRemedialClasses',
      'temporarilyNotBookable',
      'needsReview',

    ]
      .filter((key) => (key in data))
      .forEach((key) => {
        data[key] = !!data[key]
      })

    if (data.images && !Array.isArray(data.images)) {
      data.images = [data.images]
      data.images = data.images.map((image) => {
        image.banner = image.type === 'banner'
        return image
      })
    }

    if (data.links && !Array.isArray(data.links)) {
      data.links = [data.links]
    }

    return data
  }

  activate() {
    this.set({
      isDeactivated: false
    })
    return this.save()
  }

  deactivate() {
    this.set({
      isDeactivated: true
    })
    return this.save()
  }

  clone(data = {}) {
    let links
    let images
    if ('links' in data) {
      links = data.links
      delete data.links
    }
    if ('images' in data) {
      images = data.images
      delete data.images
    }
    const clone = super.clone(data)
    if (links) {
      clone.links = links
    }
    if (images) {
      clone.images = images
    }
    return clone
  }

  set(data) {

    super.set(data)

    // if (!this.id) {
    //   const rawData = {
    //     ...this.getJSON(),
    //     links: null,
    //     images: null
    //   }
    //   localStorage.guidedTourDraft = JSON.stringify(rawData)
    // }

  }

  save(opts = {}) {

    opts.contentType = 'application/json'
    if (!opts.action) {
      opts.action = 'upload'
      opts.contentType = 'form-data'
    }
    opts.method = 'post'

    if (!this.id) {
      opts.id = 'new'
    }
    return super.save(opts)
      .then((res) => {
        // delete localStorage.guidedTourDraft
        return res
      })
  }

  @computed get asJSON() {

    return {
      id: this.id,
      museumId: this.museumId,
      title: this.title,
      sanitizedOrderTitle: this.sanitizedOrderTitle,
      isDeactivated: this.isDeactivated,
      duration: this.duration,
      description: this.description,
      generalPrice: this.generalPrice,
      minPrice: this.minPrice,
      costsByPerson: this.costsByPerson,
      bookingStart: this.bookingStart,
      bookingEnd: this.bookingEnd,
      bookableOutOfSeason: this.bookableOutOfSeason,
      bookableOutOfOpeningTimes: this.bookableOutOfOpeningTimes,
      specialConditionsForRemedialClasses: this.specialConditionsForRemedialClasses,
      freeEntryForTeachersAtPrevisit: this.freeEntryForTeachersAtPrevisit,
      meetingPoint: this.meetingPoint,
      maxParticipants: this.maxParticipants,
      expireAt: this.expireAt,
      curriculums: this.curriculums,
      grades: toJS(this.grades),
      subjects: toJS(this.subjects),
      days: toJS(this.days),
      schools: toJS(this.schools),
      languages: this.sequelizeLanguage(this.languages),
      links: toJS(this.links),
      images: toJS(this.images),
      hostelUrl: this.hostelUrl,
      temporarilyNotBookable: this.temporarilyNotBookable,
      temporarilyNotBookableReason: this.temporarilyNotBookableReason,
      // eslint-disable-next-line no-underscore-dangle
      _message: this._message,
    }

  }

  sequelizeLanguage(languages) {
    return (languages || [])
      .filter((lang) => !!toJS(lang))
      .map((lang) => {
        return (typeof lang === 'object'
          ? getValue(lang.value || lang.id)
          : lang)
      })
      .filter((lang) => {
        const l = toJS(lang)
        return l !== null && l !== undefined
      })
      .join(',')
  }

  getJSON() {
    return this.asJSON
  }

  matches(filter) {

    if (!filter.search) {
      return true
    }

    const search = filter.search.toLowerCase()

    return `${
      this.title
    } ${
      this.subtitle
    } ${
      this.description
    }`.toLowerCase().indexOf(search) > -1

  }

}



// WEBPACK FOOTER //
// ./src/guided-tour/model.js